import { inject, Injectable } from '@angular/core';
import { CapacityReportStatus } from '@libs/services/graphql/generated/ala-backend.service';
import { FacilityBedsDto } from '../../dtos-v2/facility-beds.dto';
import { CapacityTransformerService } from '../../transformers-v2/capacity.transformer.service';
import { FacilityBedsRawType } from '../../types-v2/raws/facility-beds-raw.type';
import { CapacityService } from '../capacity.service';
import { BaseSignalStore, BaseState } from './base/base-signal-store.service';
@Injectable({ providedIn: 'root' })
export class FacilityCapacityEntryStateService extends BaseSignalStore<IFacilityBedsEditingState> {
  private readonly _capacityService = inject(CapacityService); // Service to fetch data from backend
  private readonly _transformerService = inject(CapacityTransformerService);

  constructor() {
    super({
      // Initial state
      selectedReportId: '',
      selectedFacilityBeds: undefined,
      selectedReasons: [],
      entryChanged: false,
      isFirstWeekReportInFacility: false,
      noChangeChecked: false,
      //isReasonsForChange: false,
      isDrawerOpen: false,
      isLoading: false,
      error: '',
    });
  }

  //#region === SETTERS ====
  set setDrawerOpenClose(isDrawerOpen: boolean) {
    this.patchState({ isDrawerOpen });
  }
  set setSelectedReportId(selectedReportId: string) {
    this.patchState({ selectedReportId });
  }
  set setNoChangeChecked(noChangeChecked: boolean) {
    this.patchState({ noChangeChecked });
  }
  // set setIsReasonsForChange(isReasonsForChange: boolean) {
  //   this.patchState({ isReasonsForChange });
  // }
  set setSelectedFacilityBeds(selectedFacilityBeds: FacilityBedsDto) {
    this.patchState({ selectedFacilityBeds });
  }
  set setIsFirstWeekReportInFacility(isFirstWeekReportInFacility: boolean) {
    this.patchState({ isFirstWeekReportInFacility });
  }
  set setEntryChanged(entryChanged: boolean) {
    this.patchState({ entryChanged });
  }
  set setFacilitySmokingPolicy(facilityHasSmokingPolicy: boolean | undefined) {
    // Update the local state first
    const currentEntry = this._state().selectedFacilityBeds;
    if (!currentEntry) return;

    const selectedFacilityBeds: FacilityBedsDto = {
      ...currentEntry,
      facilityHasSmokingPolicy,
    };
    this.patchState({ selectedFacilityBeds });
  }
  set setSelectedReasons(selectedReasons: string[]) {
    this.patchState({ selectedReasons });
  }
  // set setReasonForChange(reasonsForChange: string[]) {
  //   // Update the local state first
  //   const currentEntry = this._state().selectedFacilityBeds;
  //   if (!currentEntry) return;

  //   const selectedFacilityBeds: FacilityBedsDto = {
  //     ...currentEntry,
  //     reasonsForChange,
  //   };
  //   this.patchState({ selectedFacilityBeds });
  // }
  // set setOtherReasonForChange(otherReasonForChange: string) {
  //   // Update the local state first
  //   const currentEntry = this._state().selectedFacilityBeds;
  //   if (!currentEntry) return;

  //   const selectedFacilityBeds: IBedEntryModel = {
  //     ...currentEntry,
  //     otherReasonForChange,
  //   };
  //   this.patchState({ selectedFacilityBeds });
  // }
  //#endregion === SETTERS ====

  //#region === SELECTORS ===
  readonly selectedReportId = this.select((s) => s.selectedReportId);
  readonly isDrawerOpen = this.select((s) => s.isDrawerOpen);
  readonly entryChanged = this.select((s) => s.entryChanged);
  readonly isFirstWeekReportInFacility = this.select(
    (s) => s.isFirstWeekReportInFacility
  );
  readonly selectedFacilityName = this.select(
    (s) => s.selectedFacilityBeds?.facilityName || ''
  );
  readonly selectedFacilityStatus = this.select(
    (s) => s.selectedFacilityBeds?.status || ''
  );
  readonly facilitySmokingPolicy = this.select(
    (s) => s.selectedFacilityBeds?.facilityHasSmokingPolicy
  );
  readonly selectedFacilityBeds = this.select((s) => s.selectedFacilityBeds);
  // readonly selectedCapacityEntryRows = this.select(
  //   () => this._entryRowStateService.selectFacilityBedsRows
  // );
  readonly selectedReasons = this.select((s) => s.selectedReasons);
  // readonly existingReasons = this.select(
  //   (s) => s.selectedFacilityBeds?.reasonsForChange || []
  // );
  readonly noChangeChecked = this.select((s) => s.noChangeChecked);
  //readonly isReasonsForChange = this.select((s) => s.isReasonsForChange);
  // readonly chkNoChangesStateSignal = this.select((s) => {
  //   const isInitialDataExist =
  //     this._entryRowStateService.isInitialDataExistSignal();
  //   if (!this.selectedFacilityBeds()) {
  //     return {
  //       visible: false,
  //       enabled: false,
  //       checked: false,
  //     };
  //   }
  //   const visible =
  //     s.selectedFacilityBeds?.status === CapacityReportStatus.INPROGRESS ||
  //     isInitialDataExist;
  //   const enabled =
  //     s.selectedFacilityBeds?.status === CapacityReportStatus.NOTSTARTED;
  //   const checked = this.noChangeChecked();

  //   return {
  //     visible,
  //     enabled,
  //     checked,
  //   };
  // });
  // readonly showOtherReasons = this.select(
  //   (state) => state.selectedFacilityBeds?.hasOtherReasonForChange
  // );
  // readonly reasonsForChangeEnabledSignal = this.select((s) =>
  //   s.selectedFacilityBeds?.dataEntryRows.some(
  //     (row) =>
  //       row.operationalBeds?.previousBeds !== undefined &&
  //       row.operationalBeds?.previousBeds !== row.operationalBeds.currentBeds
  //   )
  // );
  readonly reportCurrentState = this.select((s) => {
    // If user is currently typing/changing something, always show INPROGRESS

    // If any data has been touched/changed, show INPROGRESS
    if (this.entryChanged()) {
      return CapacityReportStatus.INPROGRESS;
    }

    // Otherwise, use the status from dataEntry if available
    if (s) {
      return s.selectedFacilityBeds?.status;
    }

    return CapacityReportStatus.NOTSTARTED;
  });
  // readonly isSubmittable = this.select((s) =>
  //   this._validatorService.isSubmittable(
  //     s.selectedFacilityBeds!,
  //     s.entryChanged?.changingOtherReasonLength,
  //     this.reasonsForChangeEnabledSignal() ?? false,
  //     this.chkNoChangesStateSignal().checked ?? false,
  //     this.reportCurrentState() ?? CapacityReportStatus.NOTSTARTED
  //   )
  // );
  //#endregion  === SELECTORS ===

  //#region === ACTIONS ===
  resetToInitialState(): void {
    this.reset({
      // Initial state
      selectedReportId: '',
      selectedFacilityBeds: undefined,
      selectedReasons: [],
      entryChanged: false,
      isFirstWeekReportInFacility: false,
      noChangeChecked: false,
      isDrawerOpen: false,
      isLoading: false,
      error: '',
    });
  }
  // async submit(context: {
  //   areReasonsForChangeEnabled: boolean;
  //   isOtherReasonEnabled: boolean;
  //   facilityHasSmokingPolicy: boolean;
  // }) {
  //   if (!this.selectedFacilityId) return;

  //   if (!context.areReasonsForChangeEnabled) {
  //     await this._entryRowStateService.saveChangedValues([
  //       { name: 'reasonsForChange', value: [] },
  //       { name: 'hasOtherReasonForChange', value: false },
  //       { name: 'otherReasonForChange', value: '' },
  //     ]);
  //   } else if (!context.isOtherReasonEnabled) {
  //     await this._entryRowStateService.saveChangedValues([
  //       { name: 'hasOtherReasonForChange', value: false },
  //       { name: 'otherReasonForChange', value: '' },
  //     ]);
  //   }
  //   await this.submitCapacityReport();
  //   // TO-DO: move to Component
  //   // await this._reportListStateService.loadCapacityReports();
  //   this.setDrawerOpenClose = false;
  // }

  // async saveReasonForChange(reasons: string[]) {
  //   // Update the local state first
  //   //this.setReasonForChange = reasons;

  //   // Persist the changes
  //   await this._entryRowStateService.saveChangedValues([
  //     { name: 'reasonsForChange', value: reasons },
  //     { name: 'hasOtherReasonForChange', value: reasons.includes('Other') },
  //   ]);
  // }

  // async saveOtherReasonForChange(otherReason: string) {
  //   // Update the local state first
  //   //this.setOtherReasonForChange = otherReason;

  //   // Persist the changes
  //   await this._entryRowStateService.saveChangedValues([
  //     { name: 'otherReasonForChange', value: otherReason.trim() },
  //   ]);
  // }

  /** Load reports and count asynchronously */
  async submitCapacityReport() {
    return this.executeAsync(async () => {
      // Fetch report list
      if (
        this.selectedReportId() &&
        this.selectedReportId().length > 0 &&
        this.facilitySmokingPolicy !== undefined
      ) {
        const report = await this._capacityService.submitCapacityReport(
          this.selectedReportId(),
          this.facilitySmokingPolicy() !== true
            ? this.facilitySmokingPolicy() ?? false
            : true // the smoking policy should not be changed when selected YES already
        );

        if (report) {
          // Transform capacity report raw data to dto
          const transformedDto =
            this._transformerService.transformToFacilityBedsDto(
              report as FacilityBedsRawType
            );

          // const converted =
          //   this._mapper.mapCapacityReportToBedEntryModel(report);
          // this.setSelectedFacilityBeds = converted;
          if (
            transformedDto &&
            transformedDto.facilityBeds &&
            transformedDto.facilityBeds.length > 0
          ) {
            // this._entryRowStateService.setCapacityEntryRows =
            //   converted.dataEntryRows.filter((row) => row.fundedBeds > 0);
            // this._entryRowStateService.setStatus = converted.status;
            // TO-DO: move to component. Update the report list state as well
            // this._reportListStateService.updateCapacityReportStatus(
            //   this.selectedFacilityId(),
            //   transformedDto.status
            // );
          }
        }
      }
    });
  }

  async resolveFirstWeekFlagForCurrentFacility() {
    return this.executeAsync(async () => {
      // Fetch report list
      if (this.selectedReportId() && this.selectedReportId().length > 0) {
        const firstWeekReportId =
          await this._capacityService.fetchCapacityReportFirstWeekId(
            this.selectedReportId()
          );
        if (firstWeekReportId) {
          this.setIsFirstWeekReportInFacility =
            firstWeekReportId === this.selectedReportId();
        }
      }
    });
  }

  async loadCapacityReportByFacilityId() {
    return this.executeAsync(async () => {
      // Fetch report list
      if (this.selectedReportId() && this.selectedReportId().length > 0) {
        const report = await this._capacityService.fetchCapacityReport(
          this.selectedReportId()
        );
        if (report) {
          // Transform capacity report raw data to dto
          const transformedDto =
            this._transformerService.transformToFacilityBedsDto(
              report as FacilityBedsRawType
            );
          if (
            transformedDto &&
            transformedDto.facilityBeds &&
            transformedDto.facilityBeds.length > 0
          ) {
            // Get valid bed types only
            const validBedsDto = transformedDto.facilityBeds.filter(
              (r) => r.fundedBedCount && r.fundedBedCount > 0
            );
            transformedDto.facilityBeds = validBedsDto;
            this.setSelectedFacilityBeds = transformedDto;
            // this._entryRowStateService.setCapacityEntryRows =
            //   transformedDto.facilityBeds.filter((row) => row.fundedBeds > 0);
            // this._entryRowStateService.setStatus = transformedDto.status;
          }
        }
      }
    });
  }
  //#endregion === ACTIONS ===
}

/** State interface for CapacityReportListStateService */
export interface IFacilityBedsEditingState extends BaseState {
  selectedReportId: string;
  selectedFacilityBeds?: FacilityBedsDto;
  selectedReasons: string[];
  isFirstWeekReportInFacility: boolean;
  noChangeChecked?: boolean;
  entryChanged?: boolean;
  isDrawerOpen: boolean;
}
